import React, { useMemo, useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Grid, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";
import DataTable from "../../components/common/DataTable";
import { genericGet } from "../../services/apiServices";
import { GET_SUPPLIER_LEDGER, SEARCH_CLIENT_COMPANY } from "../../services/apiRoutes";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../common/CustomLoader";
import CustomReport from "../common/CustomReport";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import ClearRounded from '@mui/icons-material/ClearRounded';
import { genericPost } from "../../services/authServices";
import { getColorById, getColorPlate, getCurrencyList } from "../../services/CommonServices";
import CustomFilter from "../common/CustomFilter";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import debounce from "lodash/debounce";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";
import CustomStatus from "../common/CustomStatus";


const title = "Supplier Ledger"
const SupplierLedger = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [permission, setPermissions] = useState()
    const [currencyList, setCurrencyList] = useState([]);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [colorlist, setColorlist] = useState([]);
    const [filterPayload, setFilterPayload] = useState({
        currencyId: '',
        clientCompanyId: '',
    });
    const [searchCompany, setSearchCompany] = useState([]);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])


    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                currencyId: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);

    const getCommonData = async () => {
        const currencyListData = await getCurrencyList();
        setCurrencyList(currencyListData);
        const colorData = await getColorPlate();
        setColorlist(colorData);
    }


    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },
            {
                accessorKey: 'clientCompanyName', header: 'Supplier Name', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },

            {
                accessorKey: "billingCountry",
                header: "Country",
                tableStyle: { textAlign: "center" },
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <CustomStatus
                            title={row.original.billingCountry}
                            sx={{
                                backgroundColor: row.original.countryIdBackground,
                                color: row.original.countryIdColor,
                                border: `1px solid ${row.original.countryIdColor}`,
                                textAlign: "center",
                                display: "inline-block",
                            }}
                        />
                    );
                },
                enableSorting: false,
            },

            {
                accessorKey: 'contactPerson', header: 'Contact Person', size: 150, tableHeader: {
                    justifyContent: 'flex-start',
                }, footer: 'Total',
            },
            {
                accessorKey: 'currency', header: 'Currency', size: 100, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            { accessorKey: 'totalAmount', header: 'Billing Amount', size: 50, tableStyle: { textAlign: "center" }, footer: '00.00', },
            { accessorKey: 'paymentAmount', header: 'Payment Amount', size: 50, tableStyle: { textAlign: "center" }, footer: '00.00', },
            { accessorKey: 'balanceAmount', header: 'Balance Amount', size: 50, tableStyle: { textAlign: "center" }, footerCellStyle: { textAlign: "center" }, footer: '00.00', },
            {
                accessorKey: "action",
                header: "Action",
                size: 50,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px' }}>

                        <IconButton onClick={() => handleViewClick(row.original.clientCompanyId, row.original.currencyId)}>
                            <VisibilityIcon />
                        </IconButton>

                    </div>
                ),
            },
        ],
        [permission]
    );
    const handleViewClick = (clientCompanyId, currencyId) => {
        navigate(`/app/reports/supplierledger/${clientCompanyId}/${currencyId}`);
    };

    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,   // backend expects 1-based
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_SUPPLIER_LEDGER, payload);

            if (response.data.success && Array.isArray(response.data.data)) {

                const mappedData = response.data.data.map((item, idx) => {
                    const currencySymbol = item.currency_symbol || "";
                    const totalAmt = parseFloat(item.commercialAmount || 0);
                    const paymentAmt = parseFloat(item.paymentAmount || 0);
                    const balanceAmt = parseFloat(item.balanceAmount || 0);

                    const countryColorObj = getColorById(item?.clientCompanyId, colorlist, {
                        matchKey: "colorId",
                        prefixLength: 1,
                    });


                    return {
                        index: (idx + 1).toString().padStart(2, "0"),
                        clientCompanyId: item.clientCompanyId,
                        clientCompanyName: item.companyName || "",
                        billingCountry: item.mailingCountryName || "",
                        currencyId: item.currencyId || "",
                        currency: item.currency_name || "",

                        totalAmount: `${currencySymbol} ${totalAmt.toFixed(2)}`,
                        paymentAmount: `${currencySymbol} ${paymentAmt.toFixed(2)}`,
                        balanceAmount: `${currencySymbol} ${balanceAmt.toFixed(2)}`,
                        countryIdColor: countryColorObj.color || '',
                        countryIdBackground: countryColorObj.backgroundColor || '',
                    };
                });

                setData(mappedData);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError("No supplier ledger data found");
            }

        } catch (error) {
            console.error("Error fetching supplier ledger", error);
            setError("Error fetching supplier ledger");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchData(page, pageSize)
    }, [page, pageSize, colorlist, token])

    // Function to fetch client companies by name
    const fetchClientCompanies = async (query) => {
        setLoading(true);
        try {
            const url = `${SEARCH_CLIENT_COMPANY}?clientCompanyName=${encodeURIComponent(query || "")}`;
            const response = await genericPost(url);
            if (response.data.success && response.data.clientCompanies) {
                const options = response.data.clientCompanies.map(company => ({
                    id: company.clientCompanyId,
                    title: company.clientCompanyName,
                }));
                setSearchCompany(options);
            } else {
                setSearchCompany([]);
                setError("No client companies found");
            }
        } catch (err) {
            console.error(err);
            setError("Error fetching client companies");
            setSearchCompany([]);
        } finally {
            setLoading(false);
        }
    };

    // Debounce API calls while typing
    const debouncedFetch = useMemo(() => debounce(fetchClientCompanies, 500), []);
    useEffect(() => {
        fetchClientCompanies("");
        return () => debouncedFetch.cancel();
    }, []);


    const defaultView = 'supplierledger';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Filter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
            <CustomFilter
                drawerTitle="Filter"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={currencyList}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Currency"
                                        onChange={(event, newValue) => handleFilterChange("currencyId", newValue?.id)}
                                        value={currencyList.find((item) => item.id === filterPayload.currencyId) || null}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={searchCompany}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Supplier"
                                        value={searchCompany.find((item) => item.id === filterPayload.clientCompanyId) || null}
                                        onInputChange={(e, value, reason) => {
                                            if (reason === "input") {
                                                debouncedFetch(value);
                                            }
                                        }}
                                        onChange={(e, newValue) => handleFilterChange("clientCompanyId", newValue?.id)}
                                        loading={loading}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        style={cif_fob_style}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );


    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}

        </>
    );
};
export default SupplierLedger;
const cif_fob_style = {
    backgroundColor: '#ffff',
    color: '#007bff',
    textTransform: "capitalize",
    borderRadius: 4,
    border: '1px solid #007bff',
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: "100px"

}
